/**
 * Theme CSS variable injection — supports both platform defaults and org overrides.
 *
 * Takes color hex values and generates full 50-950 shade palettes,
 * then injects them as CSS variables on :root.
 */

/** Legacy org-only theme (kept for backward compat) */
export interface OrgTheme {
  orgId: string;
  logoUrl: string | null;
  logoMarkUrl: string | null;
  faviconUrl: string | null;
  primaryColor: string | null;
  secondaryColor: string | null;
  accentColor: string | null;
  fontFamily: string | null;
  fontUrl: string | null;
  customCss: string | null;
  darkModeEnabled: boolean;
}

/** Full resolved theme (platform + org merged) */
export interface ResolvedTheme {
  primaryColor: string | null;
  secondaryColor: string | null;
  accentColor: string | null;
  successColor: string | null;
  warningColor: string | null;
  errorColor: string | null;
  infoColor: string | null;
  grayColor: string | null;
  headingFontFamily: string | null;
  bodyFontFamily: string | null;
  headingFontSize: string | null;
  bodyFontSize: string | null;
  headingFontUrl: string | null;
  bodyFontUrl: string | null;
  logoUrl: string | null;
  logoMarkUrl: string | null;
  faviconUrl: string | null;
  borderRadius: string | null;
  customCss: string | null;
  darkModeEnabled: boolean;
}

// ─── Color utilities ────────────────────────────────────────────────

/** Parse hex (#RRGGBB) to [H, S, L] */
function hexToHSL(hex: string): [number, number, number] {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;

  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (d !== 0) {
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }

  return [h * 360, s * 100, l * 100];
}

/** HSL to RGB values (0-255) */
function hslToRGB(h: number, s: number, l: number): [number, number, number] {
  const sNorm = s / 100;
  const lNorm = l / 100;
  const c = (1 - Math.abs(2 * lNorm - 1)) * sNorm;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = lNorm - c / 2;

  let r = 0, g = 0, b = 0;
  if (h < 60) { r = c; g = x; }
  else if (h < 120) { r = x; g = c; }
  else if (h < 180) { g = c; b = x; }
  else if (h < 240) { g = x; b = c; }
  else if (h < 300) { r = x; b = c; }
  else { r = c; b = x; }

  return [
    Math.round((r + m) * 255),
    Math.round((g + m) * 255),
    Math.round((b + m) * 255),
  ];
}

/**
 * Generate a 50-950 shade palette from a single hex color.
 *
 * The input color's lightness is clamped to [35, 60] and used as the
 * shade-500 anchor. Lighter shades interpolate toward 97% (near-white),
 * darker shades toward 9% (near-black), with fixed endpoints at
 * shade 50 (97%) and shade 950 (9%).
 *
 * This ensures:
 *   - Dark inputs like #0d8080 (L=28) keep their character at shade 500
 *   - Light inputs like #e2e4e8 (L=90) produce sufficiently dark 900 shades
 *   - shade 900 is always dark enough for foreground text
 *   - shade 50 is always light enough for dark-mode foreground
 *
 * Reference validation (hand-tuned CSS targets):
 *   primary #0d8080 (L=28→35): 500≈36, 900≈15  ✓
 *   gray    #e2e4e8 (L=90→60): 500≈56, 900≈15  ✓
 *   secondary #f59e0b (L=50):  500=50, 900≈15   ✓
 */
export function generatePalette(hex: string): Record<string, string> {
  const [h, s, l] = hexToHSL(hex);

  // Clamp the input lightness to a usable mid-range for shade 500.
  // Dark colors (L<35) get lifted; light colors (L>60) get pulled down.
  const l500 = Math.min(Math.max(l, 35), 60);

  const lightEnd = 97;  // shade 50 target
  const darkEnd = 9;    // shade 950 target

  const lerp = (from: number, to: number, t: number) => from + (to - from) * t;

  const shades: Record<number, number> = {
    // Light shades: lerp from l500 toward lightEnd (97)
    50:  lightEnd,
    100: lerp(l500, lightEnd, 0.90),
    200: lerp(l500, lightEnd, 0.75),
    300: lerp(l500, lightEnd, 0.55),
    400: lerp(l500, lightEnd, 0.30),
    // Anchor
    500: l500,
    // Dark shades: lerp from l500 toward darkEnd (9)
    600: lerp(l500, darkEnd, 0.30),
    700: lerp(l500, darkEnd, 0.50),
    800: lerp(l500, darkEnd, 0.68),
    900: lerp(l500, darkEnd, 0.82),
    950: darkEnd,
  };

  const result: Record<string, string> = {};
  for (const [shade, lightness] of Object.entries(shades)) {
    const [r, g, b] = hslToRGB(h, s, lightness);
    result[shade] = `${r} ${g} ${b}`;
  }
  return result;
}

// ─── Border radius mapping ──────────────────────────────────────────

const RADIUS_MAP: Record<string, Record<string, string>> = {
  none: { sm: '0px', base: '0px', md: '0px', lg: '0px', xl: '0px', '2xl': '0px', '3xl': '0px' },
  sm:   { sm: '0.125rem', base: '0.25rem', md: '0.25rem', lg: '0.375rem', xl: '0.5rem', '2xl': '0.75rem', '3xl': '1rem' },
  md:   { sm: '0.125rem', base: '0.25rem', md: '0.375rem', lg: '0.5rem', xl: '0.75rem', '2xl': '1rem', '3xl': '1.5rem' },
  lg:   { sm: '0.25rem', base: '0.375rem', md: '0.5rem', lg: '0.75rem', xl: '1rem', '2xl': '1.5rem', '3xl': '2rem' },
  xl:   { sm: '0.375rem', base: '0.5rem', md: '0.75rem', lg: '1rem', xl: '1.5rem', '2xl': '2rem', '3xl': '2.5rem' },
};

// ─── CSS variable injection ─────────────────────────────────────────

let injectedStyleEl: HTMLStyleElement | null = null;
let injectedFontLinks: HTMLLinkElement[] = [];

/** Build CSS lines for a color palette */
function addPaletteLines(lines: string[], prefix: string, hex: string): void {
  const palette = generatePalette(hex);
  for (const [shade, rgb] of Object.entries(palette)) {
    lines.push(`  --color-${prefix}-${shade}: ${rgb};`);
  }
}

/** Load a Google Font or custom font URL */
function loadFont(family: string, url?: string | null): HTMLLinkElement {
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = url || `https://fonts.googleapis.com/css2?family=${encodeURIComponent(family)}:wght@400;500;600;700&display=swap`;
  link.setAttribute('data-org-theme', 'font');
  document.head.appendChild(link);
  return link;
}

/**
 * Apply a resolved theme by injecting CSS variables.
 * Supports full ResolvedTheme or legacy OrgTheme.
 */
export function applyTheme(theme: ResolvedTheme | null): void {
  // Remove previous injections
  if (injectedStyleEl) {
    injectedStyleEl.remove();
    injectedStyleEl = null;
  }
  for (const link of injectedFontLinks) link.remove();
  injectedFontLinks = [];

  if (!theme) return;

  const lines: string[] = [];

  // Color palettes
  const colorMap: Array<[string, string | null]> = [
    ['primary', theme.primaryColor],
    ['secondary', theme.secondaryColor],
    ['accent', theme.accentColor],
    ['success', theme.successColor],
    ['warning', theme.warningColor],
    ['error', theme.errorColor],
    ['info', theme.infoColor],
    ['gray', theme.grayColor],
  ];

  for (const [prefix, hex] of colorMap) {
    if (hex) {
      addPaletteLines(lines, prefix, hex);
      if (prefix === 'primary') lines.push(`  --color-brand-primary: ${generatePalette(hex)['500']};`);
      if (prefix === 'secondary') lines.push(`  --color-brand-secondary: ${generatePalette(hex)['500']};`);
    }
  }

  // Heading font
  if (theme.headingFontFamily) {
    lines.push(`  --font-heading: '${theme.headingFontFamily}', system-ui, -apple-system, sans-serif;`);
    injectedFontLinks.push(loadFont(theme.headingFontFamily, theme.headingFontUrl));
  }

  // Body font
  if (theme.bodyFontFamily) {
    lines.push(`  --font-sans: '${theme.bodyFontFamily}', system-ui, -apple-system, sans-serif;`);
    // Only load if different from heading font
    if (theme.bodyFontFamily !== theme.headingFontFamily) {
      injectedFontLinks.push(loadFont(theme.bodyFontFamily, theme.bodyFontUrl));
    }
  }

  // Heading font size
  if (theme.headingFontSize) {
    lines.push(`  --text-heading-base: ${theme.headingFontSize};`);
  }

  // Body font size
  if (theme.bodyFontSize) {
    lines.push(`  --text-base: ${theme.bodyFontSize};`);
  }

  // Border radius
  if (theme.borderRadius && RADIUS_MAP[theme.borderRadius]) {
    const radii = RADIUS_MAP[theme.borderRadius];
    for (const [size, value] of Object.entries(radii)) {
      lines.push(`  --radius-${size}: ${value};`);
    }
  }

  // Custom CSS
  let customBlock = '';
  if (theme.customCss) {
    customBlock = `\n/* Custom CSS */\n${theme.customCss}`;
  }

  if (lines.length > 0 || customBlock) {
    // SPECIFICITY BOOST: the baseline palette is defined in
    // src/design/css-variables.css as `:root { --color-primary-500: ... }`
    // (specificity 0,1,0). If we emit `:root { ... }` here, the two rules
    // have equal specificity and the one later in the document wins.
    //
    // In Vite dev mode (HMR) and on route transitions, CSS can be re-injected
    // into <head> AFTER this runtime <style>, which silently reverts our
    // overrides to the baseline teal. That is exactly what made the ROROS
    // org preset preview look like nothing happened on click — applyTheme
    // *was* firing with the right values (confirmed by console logging),
    // the browser just preferred the later baseline stylesheet.
    //
    // Using `:root:root` doubles the pseudo-class count to specificity
    // (0,2,0), so this rule always outranks anything selecting `:root` or
    // `html` regardless of document order. No !important needed.
    const css = lines.length > 0
      ? `:root:root {\n${lines.join('\n')}\n}${customBlock}`
      : customBlock;

    injectedStyleEl = document.createElement('style');
    injectedStyleEl.setAttribute('data-org-theme', 'vars');
    injectedStyleEl.textContent = css;
    document.head.appendChild(injectedStyleEl);
  }

  // Favicon
  if (theme.faviconUrl) {
    const existing = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (existing) {
      existing.href = theme.faviconUrl;
    } else {
      const link = document.createElement('link');
      link.rel = 'icon';
      link.href = theme.faviconUrl;
      document.head.appendChild(link);
    }
  }
}

/** Legacy compat — apply OrgTheme by converting to ResolvedTheme */
export function applyOrgTheme(theme: OrgTheme | null): void {
  if (!theme) {
    applyTheme(null);
    return;
  }
  applyTheme({
    primaryColor: theme.primaryColor,
    secondaryColor: theme.secondaryColor,
    accentColor: theme.accentColor,
    successColor: null,
    warningColor: null,
    errorColor: null,
    infoColor: null,
    grayColor: null,
    headingFontFamily: null,
    bodyFontFamily: theme.fontFamily,
    headingFontSize: null,
    bodyFontSize: null,
    headingFontUrl: null,
    bodyFontUrl: theme.fontUrl,
    logoUrl: theme.logoUrl,
    logoMarkUrl: theme.logoMarkUrl,
    faviconUrl: theme.faviconUrl,
    borderRadius: null,
    customCss: theme.customCss,
    darkModeEnabled: theme.darkModeEnabled,
  });
}

/** Remove all theme overrides (revert to CSS defaults) */
export function clearOrgTheme(): void {
  applyTheme(null);
}
